<template>
  <div class="container-wrapper">
    <div class="full-width">
      <edgesCards />
      <div class="grid grid-2">
        <section class="edges-evol-section">
          <h3>Open PnL</h3>
          <apexchart v-if="points.length" :key="'open-' + chartKey" type="line" height="280" :options="openOptions" :series="openSeries" />
          <p v-else class="edges-evol-empty">Nenhum evento de hoje com snapshot.</p>
        </section>
        <section class="edges-evol-section">
          <h3>Locked Basis PnL</h3>
          <apexchart v-if="points.length" :key="'locked-' + chartKey" type="line" height="280" :options="lockedOptions" :series="lockedSeries" />
          <p v-else class="edges-evol-empty">Nenhum evento de hoje com snapshot.</p>
        </section>
        <section class="edges-evol-section">
          <h3>Performance Estrutural</h3>
          <div class="edges-evol-tabs" role="tablist">
            <button type="button" role="tab" :aria-selected="structuralTab === 'locked'" :class="{ active: structuralTab === 'locked' }" @click="structuralTab = 'locked'">Locked Basis</button>
            <button type="button" role="tab" :aria-selected="structuralTab === 'structural'" :class="{ active: structuralTab === 'structural' }" @click="structuralTab = 'structural'">Estrutural</button>
            <button type="button" role="tab" :aria-selected="structuralTab === 'net'" :class="{ active: structuralTab === 'net' }" @click="structuralTab = 'net'">Líquido</button>
            <strong v-if="structuralLastLabel" class="edges-evol-last" :style="{ color: structuralTabColor }">{{ structuralLastLabel }}</strong>
          </div>
          <apexchart v-if="points.length" :key="structuralTab + '-' + chartKey" type="line" height="280" :options="structuralOptions" :series="structuralSeries" />
          <p v-else class="edges-evol-empty">Nenhum evento de hoje com snapshot.</p>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { defaultChartOptions } from '@/utils/globalApexConfig'
import { buildEdgesSeries } from '@/utils/computeEdgesSeries.js'
import edgesCards from '@/components/edgesCards.vue'

function formatSignedUsd(val, withPlus = false) {
  const n = Number(val)
  if (!Number.isFinite(n)) return '—'
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (n < 0) return `-$${abs}`
  return withPlus && n > 0 ? `+$${abs}` : `$${abs}`
}

function formatClock(ts) {
  if (!Number.isFinite(ts)) return ''
  return new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function lineOptions(color, seriesName, lastIndex = -1) {
  const markLast = lastIndex >= 0
  return {
    ...defaultChartOptions,
    colors: [color],
    legend: { show: false },
    stroke: { curve: 'straight', width: 2 },
    markers: {
      size: 3,
      hover: { size: 4 },
      strokeWidth: 0,
      discrete: markLast ? [{
        seriesIndex: 0,
        dataPointIndex: lastIndex,
        size: 7,
        fillColor: color,
        strokeColor: '#fff',
        strokeWidth: 2,
      }] : [],
    },
    dataLabels: {
      enabled: markLast,
      offsetX: -6,
      offsetY: -10,
      formatter(val, opts) {
        if (opts.dataPointIndex !== lastIndex) return ''
        return formatSignedUsd(val, true)
      },
      style: {
        fontSize: '11px',
        fontWeight: 600,
        colors: [color],
      },
      background: {
        enabled: true,
        foreColor: '#fff',
        borderRadius: 4,
        padding: 4,
        borderWidth: 0,
        opacity: 0.85,
        dropShadow: { enabled: false },
      },
    },
    grid: {
      ...defaultChartOptions.grid,
      padding: { top: markLast ? 20 : 0, right: markLast ? 36 : 8 },
    },
    xaxis: {
      type: 'datetime',
      labels: { datetimeUTC: false },
    },
    yaxis: {
      labels: { formatter: (val) => formatSignedUsd(val) },
    },
    tooltip: {
      shared: false,
      intersect: false,
      custom({ series, seriesIndex, dataPointIndex, w }) {
        const value = series?.[seriesIndex]?.[dataPointIndex]
        const ts = w?.globals?.seriesX?.[seriesIndex]?.[dataPointIndex]
        const isLast = dataPointIndex === lastIndex
        return `<div style="background:#1e1e1e;color:#fff;padding:8px 10px;border:1px solid #444;border-radius:6px;font-size:12px;">
          <div style="opacity:.75;margin-bottom:4px;">${formatClock(ts)}${isLast ? ' · último' : ''}</div>
          <div><b style="color:${color}">${seriesName}</b> ${formatSignedUsd(value, true)}</div>
        </div>`
      },
    },
  }
}

const STRUCTURAL_TABS = {
  locked: { name: 'Locked Basis', color: '#a78bfa', field: 'lockedBasisPnl' },
  structural: { name: 'Estrutural', color: '#38bdf8', field: 'structuralPnl' },
  net: { name: 'Líquido', color: '#f59e0b', field: 'structuralDelta' },
}

export default {
  name: 'EdgesEvolView',
  components: { edgesCards },
  data() {
    return { structuralTab: 'net' }
  },
  computed: {
    ...mapState({ eventLogs: (state) => state.eventLogs || [] }),
    points() {
      return buildEdgesSeries(this.eventLogs)
    },
    chartKey() {
      const last = this.points[this.points.length - 1]
      return `${this.points.length}-${last ? last.ts : 0}`
    },
    openSeries() {
      return [{
        name: 'Open PnL',
        data: this.points.map((point) => [point.ts, Number(point.openPnl.toFixed(2))]),
      }]
    },
    lockedSeries() {
      return [{
        name: 'Locked Basis PnL',
        data: this.points.map((point) => [point.ts, Number(point.lockedBasisPnl.toFixed(2))]),
      }]
    },
    structuralSeries() {
      const tab = STRUCTURAL_TABS[this.structuralTab]
      return [{
        name: tab.name,
        data: this.points.map((point) => [point.ts, Number(point[tab.field].toFixed(2))]),
      }]
    },
    openOptions() {
      return lineOptions('#26a69a', 'Open PnL')
    },
    lockedOptions() {
      return lineOptions('#a78bfa', 'Locked Basis PnL')
    },
    structuralTabColor() {
      return STRUCTURAL_TABS[this.structuralTab].color
    },
    structuralLastLabel() {
      const last = this.points[this.points.length - 1]
      if (!last) return ''
      const tab = STRUCTURAL_TABS[this.structuralTab]
      return formatSignedUsd(last[tab.field], true)
    },
    structuralOptions() {
      const tab = STRUCTURAL_TABS[this.structuralTab]
      return lineOptions(tab.color, tab.name, this.points.length - 1)
    },
  },
}
</script>

<style scoped>
.edges-evol-section {
  background-color: var(--table-header-bgcolor);
  border-radius: 10px;
  padding: 20px;
  min-width: 0;
}

.edges-evol-section h3 {
  margin: 0 0 12px;
  font-size: 1.2em;
}

.edges-evol-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.edges-evol-last {
  font-size: 15px;
  font-weight: 700;
}

.edges-evol-tabs button {
  padding: 6px 18px;
  border-radius: 8px;
  border: 1px solid #4b5563;
  background: var(--table-row-bgcolor);
  color: var(--text-muted, #9ca3af);
  font-size: 13px;
  cursor: pointer;
}

.edges-evol-tabs button.active {
  border-color: #6366f1;
  background: #1e1e2e;
  color: #a5b4fc;
  font-weight: 600;
}

.edges-evol-empty {
  margin: 0;
  opacity: 0.7;
}
</style>
